/**
 * Setup Flow Types
 *
 * Defines types for tracking user progress through the setup flow.
 * Used for analytics, auto-resume, and drop-off analysis.
 */

/**
 * Setup flow step enum
 * Represents all possible steps in the user onboarding flow
 */
export type SetupFlowStep =
  | 'login'              // SetupPage - User is on login/register page
  | 'profile'            // SetupProfilePage - Enter name & avatar
  | 'purpose'            // SetupPurposePage - Select usage purpose
  | 'intent'             // SetupIntentPage - Select goals
  | 'onboarding'         // OnboardingPage - 3-slide onboarding
  | 'completed'          // Setup fully completed

/**
 * Setup flow progress data structure
 */
export interface SetupFlowProgress {
  step: SetupFlowStep
  completed: boolean
  startedAt: string
  completedAt?: string
  onboardingSlideReached: number        // 0, 1, or 2
  onboardingSlidesViewed: number[]      // Array of viewed slide indexes [0, 1, 2]
}

/**
 * Get the next step in the setup flow
 * @param currentStep - Current setup flow step
 * @returns Next step, or null if already at last step
 */
export function getNextSetupStep(currentStep: SetupFlowStep): SetupFlowStep | null {
  const stepOrder: SetupFlowStep[] = ['login', 'profile', 'purpose', 'intent', 'onboarding', 'completed']
  const currentIndex = stepOrder.indexOf(currentStep)

  if (currentIndex === -1 || currentIndex === stepOrder.length - 1) {
    return null  // Already at last step or invalid step
  }

  return stepOrder[currentIndex + 1]
}

/**
 * Get the route path for a setup flow step
 * @param step - Setup flow step
 * @returns Route path for the step
 *
 * IMPORTANT: 'login' step returns '/setup-profile' (next step after login)
 * to avoid infinite loop when navigating from SetupPage
 */
export function getRouteForStep(step: SetupFlowStep): string {
  switch (step) {
    case 'login':
      // User just logged in → navigate to profile setup (next step)
      return '/setup-profile'
    case 'profile':
      return '/setup-profile'
    case 'purpose':
      return '/setup-purpose'
    case 'intent':
      return '/setup-intent'
    case 'onboarding':
      return '/onboarding'
    case 'completed':
      return '/tasks'
    default:
      return '/setup-profile'
  }
}

/**
 * Get setup flow step from route path
 * @param path - Route path
 * @returns Setup flow step, or 'login' if not found
 */
export function getStepFromRoute(path: string): SetupFlowStep {
  switch (path) {
    case '/':
      return 'login'
    case '/setup-profile':
      return 'profile'
    case '/setup-purpose':
      return 'purpose'
    case '/setup-intent':
      return 'intent'
    case '/onboarding':
      return 'onboarding'
    case '/tasks':
    case '/home':
      return 'completed'
    default:
      return 'login'
  }
}

/**
 * Check if all onboarding slides have been viewed
 * @param slidesViewed - Array of viewed slide indexes
 * @returns True if all 3 slides (0, 1, 2) have been viewed
 */
export function hasViewedAllOnboardingSlides(slidesViewed: number[]): boolean {
  return [0, 1, 2].every(slide => slidesViewed.includes(slide))
}
